import { z } from 'zod'

/**
 * The master password protects every server credential, so it gets a real minimum. Everything
 * else about it is the user's business — no composition rules that only push people towards
 * `Passwort1!`.
 */
export const MASTER_PASSWORD_MIN_LENGTH = 12

export const vaultStatusSchema = z.object({
  /** False before the first start has set a master password. */
  initialized: z.boolean(),
  locked: z.boolean(),
  /**
   * Whether the requesting client holds a valid session. The vault being unlocked is global
   * state; being allowed to act on it is per client, and a second device must land on the
   * unlock screen rather than on an empty dashboard.
   */
  authenticated: z.boolean(),
  /** 0 = sperrt nicht von selbst; das ist die selbst gehostete Variante (DECISIONS ADR-070). */
  autoLockMinutes: z.number().int().nonnegative(),
  /** Seconds until the vault locks itself; null while it is locked. */
  locksInSec: z.number().int().nonnegative().nullable(),
})
export type VaultStatus = z.infer<typeof vaultStatusSchema>

export const setupVaultSchema = z.object({
  masterPassword: z
    .string()
    .min(
      MASTER_PASSWORD_MIN_LENGTH,
      `Das Master-Passwort braucht mindestens ${String(MASTER_PASSWORD_MIN_LENGTH)} Zeichen.`,
    ),
})

export const unlockVaultSchema = z.object({
  masterPassword: z.string().min(1, 'Bitte gib dein Master-Passwort ein.'),
  /**
   * Second factor, when one is set up (§7). It is sent together with the password rather than in
   * a second step: a half-finished login would be a state of its own, and the price -- deriving
   * the vault key twice for one attempt -- is a second, once.
   */
  totpCode: z.string().trim().min(1).max(64).optional(),
})

/** Sent when the password was right but the code was missing or wrong. */
export const TOTP_REQUIRED_CODE = 'totp_required'

/**
 * Ob sich diese Instanz nach einem Neustart selbst entsperrt (DECISIONS ADR-070). Nur die selbst
 * gehostete Variante kennt das überhaupt; auf dem Desktop gibt es keine Passwortdatei.
 */
export const autoUnlockStatusSchema = z.object({
  /** False, wenn KONTOR_MASTER_PASSWORD_FILE nicht gesetzt ist — dann ist der Rest bedeutungslos. */
  available: z.boolean(),
  enabled: z.boolean(),
  /** Der Pfad ist kein Geheimnis und beantwortet die erste Rückfrage im Fehlerfall. */
  file: z.string().nullable(),
})
export type AutoUnlockStatus = z.infer<typeof autoUnlockStatusSchema>

export const setAutoUnlockSchema = z.object({ enabled: z.boolean() })
